import 'whatwg-fetch'
import 'intersection-observer'

import LazyLoad from 'vanilla-lazyload'
import Highway from '@dogstudio/highway'
import gsap from 'gsap'
import ScrollTrigger from "gsap/ScrollTrigger";
import { throttle, debounce } from 'throttle-debounce'
import { listen } from 'quicklink/dist/quicklink.mjs'

import Menu from './util/Menu'
import Showreel from './util/Showreel'
import locomotiveScroll from 'locomotive-scroll'
import Observer from './util/Observer'

import Page from './routes/Page'

import Project from './transitions/Project'
import Fade from './transitions/Fade'
import store from './store'

gsap.registerPlugin(ScrollTrigger);

export default class App {
  constructor() {
    this.resize = this.resize.bind(this)
    this.scroll = this.scroll.bind(this)
    this.update = this.update.bind(this)

    this.resizeDebounced = debounce(100, this.resize)
    this.resizeThrottled = throttle(150, this.resize)
    this.scrollDebounced = debounce(100, this.scroll)
    this.scrollThrottled = throttle(16, this.scroll)

    store.w = {
      ww: window.innerWidth,
      wh: window.innerHeight
    }

    this.checkMobile()
    this.start()
  }

  start() {
    store.noFirstLoaded = false
    store.menu = new Menu()

    const showreel = document.querySelector('.showreel')

    showreel && (store.showreel = new Showreel())

    this.lazyLoad = new LazyLoad()
    this.initSmoothScroll()

    this.initHighway().then(() => {
      this.events()
      this.update()

      gsap.to(document.querySelector('.fade'), {
        opacity: 0,
        duration: 0.5,
        onStart: () => {
          window.scrollTo(0, 0)
        },
        onComplete: () => {
          store.smoothScroll && store.smoothScroll.update()
          this.checkAnchor()
          document.body.classList.add('has-loaded')
          store.noFirstLoaded = true
        }
      })

      const adminBarLinks = document.body.querySelectorAll('#wpadminbar a')

      App.Highway.detach(adminBarLinks)
    })
  }

  initDOMObserver() {
    this.DOMObserver = new DOMObserver(this.observerCallBack)
  }

  initObserver() {
    store.observer = new Observer()
  }

  initSmoothScroll() {
    store.scrollContainer = document.body.querySelector('.js-scroll');

    /* eslint-disable-next-line */
    store.smoothScroll = new locomotiveScroll({
      el: store.scrollContainer,
      smooth: true,
      passive: true,
      inertia: 1.0
    })

    // ScrollTrigger avec Locomotive Scroll
    ScrollTrigger.scrollerProxy(store.scrollContainer, {
      scrollTop(value) {
        return arguments.length ? store.smoothScroll.scrollTo(value, 0, 0) : store.smoothScroll.scroll.instance.scroll.y;
      },
      getBoundingClientRect() {
        return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      },
      pinType: store.scrollContainer.style.transform ? "transform" : "fixed",
    });

    store.smoothScroll.on("scroll", ScrollTrigger.update);

    ScrollTrigger.addEventListener("refresh", () => store.smoothScroll.update());
    ScrollTrigger.refresh();

    this.initObserver()
  }

  checkMobile() {
    store.isMobile && document.body.classList.remove('isMobile')
    store.isMobile && document.documentElement.classList.remove('isMobile')
    /* eslint-disable */
    store.isMobile = (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
    store.isMobile && document.body.classList.add('isMobile')
    store.isMobile && document.documentElement.classList.add('isMobile')
  }

  initHighway() {
    return new Promise((resolve) => {
      App.Highway = new Highway.Core({
          renderers: {
            page: Page
          },
          transitions: {
            default: Fade,
            contextual: {
              project: Project
            }
          }
      })

      this.setCurrentRenderer()
          .then(resolve)
    })
  }

  setCurrentRenderer() {
    return new Promise((resolve) => {
      App.Highway.properties.renderer.then(() => {
        this.currentRenderer = App.Highway.To ? App.Highway.To : App.Highway.From

        resolve(this.currentRenderer)
      })
    })
  }

  resize() {
    store.w = {
      ww: window.innerWidth,
      wh: window.innerHeight
    }

    this.currentRenderer.resize()
  }

  scroll() {
    this.currentRenderer.scroll()
    store.menu && store.menu.scroll()
  }

	update() {
		this.currentRenderer.update()
    requestAnimationFrame(this.update)
	}

  events() {
    if (store.isMobile) window.addEventListener('orientationchange', this.resize)
    else {
      window.addEventListener('resize', this.resizeThrottled)
      window.addEventListener('resize', this.resizeDebounced)
    }

    store.smoothScroll.on('scroll', this.scroll)

    App.Highway.on('NAVIGATE_IN', () => {
      this.setCurrentRenderer().then((renderer) => { document.title = renderer.properties.page.title });
    })

    App.Highway.on('NAVIGATE_OUT', ({ location }) => {
      store.menu.onPageChange(location)
      this.destroyGFModals();
    })

    App.Highway.on('NAVIGATE_END', ({ to, location }) => {
      if (typeof ga !== 'undefined') {
        ga('set', 'location', location.href);
        ga('set', 'page', location.pathname)
        ga('set', 'title', to.page.title)
        ga('send', 'pageview')
      }

      store.menu.onNavigateEnd()

      this.checkAnchor(location)
      listen({ el: to.view })
      this.lazyLoad.update()
      this.detachAdminBarLinks(to)
    })

    window.addEventListener('highwayredirect', (e) => {
      App.Highway.redirect(e.detail.url)
    })
  }

  destroyGFModals() {
    const modals = document.querySelectorAll('.form-modal-container');
    modals.forEach(modal => {
      modal.remove();
    });
  }

  detachAdminBarLinks(to) {
    const adminBarLinks = document.body.querySelectorAll('#wpadminbar a')
    const newAdminBarLinks = to.page.body.querySelectorAll('#wpadminbar a')

    const length = Math.min(adminBarLinks.length, newAdminBarLinks.length)

    for (let i = 0; i < length; i++) {
      if (adminBarLinks[i] && newAdminBarLinks[i]) {
        adminBarLinks[i].href = newAdminBarLinks[i].href
      }
    }

    App.Highway.detach(adminBarLinks)
  }


  checkAnchor(location = null) {
    const bodyClassSubmit = Array.from(document.body.classList).find((elt) => elt.includes('formsubmit'))
    let anchor = null

    if (location && location.anchor) anchor = location.anchor
    else if (bodyClassSubmit) {
      const validate = document.querySelector('#gform_confirmation_message_' + bodyClassSubmit.split('-')[1])
      const error = document.querySelector('#gform_wrapper_' + bodyClassSubmit.split('-')[1])

      if (validate) anchor = 'gform_confirmation_message_' + bodyClassSubmit.split('-')[1]
      else if (error) anchor = 'gform_wrapper_' + bodyClassSubmit.split('-')[1]
    } else {
      const idx = window.location.href.indexOf("#")
      if (idx != -1) anchor = window.location.href.substring(idx + 1 )
    }

    if (anchor) {
      const el = document.querySelector('#' + anchor)

      if (el) {
        if (store.smoothScroll) {
          store.smoothScroll.scrollTo(el)
          store.smoothScroll.update()
        } else {
          const elRect = el.getBoundingClientRect()
          window.scrollTo(0, elRect.top)
        }
      }
    }
  }
}
